import {
  type RovoContentBridgeAPI,
  type client,
} from '@atlassian/rovo-content-bridge-api';

import {
  PointerDownCommand,
  PointerMoveCommand,
  PointerUpCommand,
} from '@canvas/databases-bridge-api/commands';

type ForwardPointerEventsParams = {
  bridge: RovoContentBridgeAPI;
  localClientId: ReturnType<typeof client>;
  externalClientId: ReturnType<typeof client>;
};

export function setupPointerEventForwarding({
  bridge,
  localClientId,
  externalClientId,
}: ForwardPointerEventsParams): () => void {
  let isForwarding = false;

  const handlePointerDown = (event: PointerEvent) => {
    const target = event.target as Element;

    if (shouldSkipElement(target)) {
      return;
    }

    isForwarding = true;

    event.preventDefault();
    event.stopPropagation();

    bridge.send(PointerDownCommand, {
      senderId: localClientId,
      receiverId: externalClientId,
      ...createEventProperties(event),
    });
  };

  const handlePointerMove = (event: PointerEvent) => {
    if (!isForwarding) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    bridge.send(PointerMoveCommand, {
      senderId: localClientId,
      receiverId: externalClientId,
      ...createEventProperties(event),
    });
  };

  const handlePointerUp = (event: PointerEvent) => {
    if (!isForwarding) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    bridge.send(PointerUpCommand, {
      senderId: localClientId,
      receiverId: externalClientId,
      ...createEventProperties(event),
    });

    isForwarding = false;
  };

  const eventOptions = {
    passive: false,
    capture: true,
  };

  document.addEventListener('pointerdown', handlePointerDown, eventOptions);
  document.addEventListener('pointermove', handlePointerMove, eventOptions);
  document.addEventListener('pointerup', handlePointerUp, eventOptions);

  return () => {
    document.removeEventListener('pointerdown', handlePointerDown, eventOptions);
    document.removeEventListener('pointermove', handlePointerMove, eventOptions);
    document.removeEventListener('pointerup', handlePointerUp, eventOptions);
  };
}

const shouldSkipElement = (target: Element): boolean => {
  return (
    target.tagName === 'BUTTON' ||
    target.tagName === 'INPUT' ||
    target.tagName === 'A' ||
    target.tagName === 'SELECT' ||
    target.tagName === 'TEXTAREA' ||
    target.closest('button, input, a, select, textarea') !== null ||
    target.hasAttribute('contenteditable') ||
    target.getAttribute('role') === 'button' ||
    target.getAttribute('aria-label') === 'dragHandlerIcon'
  );
};

const createEventProperties = (event: PointerEvent) => ({
  clientX: event.clientX,
  clientY: event.clientY,
  button: event.button,
  buttons: event.buttons,
  pointerId: event.pointerId,
  pointerType: event.pointerType,
  viewportWidth: document.documentElement.clientWidth,
  viewportHeight: document.documentElement.clientHeight,
  ctrlKey: event.ctrlKey,
  shiftKey: event.shiftKey,
  altKey: event.altKey,
  metaKey: event.metaKey,
});
